import React, { useContext, useEffect } from "react";
import Heading from "@amzn/meridian/heading";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Tooltip from "@amzn/meridian/tooltip";
import { OverviewContext } from "../overview/context/OverviewContext";
import { CycleFilterContext } from "../overview/context/CycleFilterContext";
import { PVAOverviewContext } from "../overview/context/PVAOverviewContext";
import { FormattedMessage, injectIntl } from "react-intl";
import { getDocumentURL } from "../handler/ParentDataHandler";
import { getBaseUrl } from "../outboundAmzl/util/Util";
import Box from "@amzn/meridian/box";
import infoKnockoutTokens from "@amzn/meridian-tokens/base/icon/info-knockout";
import Icon from "@amzn/meridian/icon";
import AlertWithPopover from "../packageOverview/popover";
import Alert from "@amzn/meridian/alert";
import {
  SORT,
  PICK,
  STAGE,
  INDUCT,
  OCR_CLASSIFICATION_LOCALE,
} from "../overview/utils/Constants";

const FPHView = (props) => {
  const { cycles } = useContext(CycleFilterContext);
  const { pvaData } = useContext(PVAOverviewContext);
  const { viewModel, dispatch } = useContext(OverviewContext);

  const getFPHData = () => {
    getDocumentURL().then((result) => {
      const baseUrl = getBaseUrl(result);
      dispatch({
        type: "LOAD_FPH_OVERVIEW",
        data: {
          filter: cycles.selectedCycles,
          dispatcher: dispatch,
          processName: props.fph.processName,
          baseUrl: baseUrl,
        },
      });
      dispatch({
        type: "LOAD_TARGET_DATA",
        data: {
          filter: cycles.selectedCycles,
          dispatcher: dispatch,
          processName: "target",
        },
      });
      if (props.fph.processName === "induct") {
        dispatch({
          type: "LOAD_OCR_DATA",
          data: { filter: cycles.selectedCycles, dispatcher: dispatch },
        });
      }
    });
  };

  useEffect(() => {
    if (cycles.allCycles.length > 0) {
      getFPHData();
      const interval = setInterval(() => {
        if (!document.hidden) {
          getFPHData();
        }
      }, props.fph.refreshRate);
      return () => clearInterval(interval);
    }
  }, [cycles]);

  const formatAlertMessage = (id, defaultString) => {
    return props.intl.formatMessage({ id: id, defaultMessage: defaultString });
  };

  const popOverInfo = (processName) => {
    if (processName === INDUCT) {
      let ocr = viewModel.ocr["ontimeCompletionRisk"];
      return {
        title: formatAlertMessage(
          viewModel.fph[props.fph.processName].alert,
          viewModel.fph[props.fph.processName].alertMessage,
        ).concat(
          " ",
          formatAlertMessage(
            OCR_CLASSIFICATION_LOCALE[ocr.classification],
            ocr.classification,
          ),
        ),
        alertMessage: formatAlertMessage(
          ocr.prefixAlertMessage,
          ocr.prefixAlertDefaultMessage,
        ).concat(
          " ",
          ocr.value,
          " ",
          formatAlertMessage(
            ocr.postfixAlertMessage,
            ocr.postAlertDefaultMessage,
          ),
        ),

        id: "ocr.alert",

        defaultMessage: "Completion Risk",
      };
    }
  };

  // Completion Risk Alert check
  const isValidCompletionRisk = (processName) => {
    return (
      processName === INDUCT &&
      viewModel.ocr["ontimeCompletionRisk"].classification &&
      OCR_CLASSIFICATION_LOCALE[
        viewModel.ocr["ontimeCompletionRisk"].classification
      ] &&
      viewModel.fph[INDUCT].actual > 0
    );
  };

  const isActualsAlert = (processName) => {
    return (
      (processName === INDUCT &&
        viewModel.fph[INDUCT].actual < pvaData.targetData.INDUCT_FPH) ||
      (processName === SORT &&
        viewModel.fph[SORT].actual < pvaData.targetData.STOW_FPH)
    );
  };

  return (
    <Box type="outline" spacingInset="400">
      <div>
        <div className="overview-fph">
          <Heading level={2} type="h300" alignment="left">
            <FormattedMessage
              id={viewModel.fph[props.fph.processName].title}
              defaultMessage={
                viewModel.fph[props.fph.processName].defaultMessage
              }
            />
            <Tooltip
              position="right"
              title={props.intl.formatMessage({
                id: viewModel.fph[props.fph.processName].tooltip,
                defaultMessage:
                  viewModel.fph[props.fph.processName].tooltipMessage,
              })}
            >
              <Icon className={"custom-tool-tip"} tokens={infoKnockoutTokens} />
            </Tooltip>
          </Heading>
          {isValidCompletionRisk(props.fph.processName) ? (
            <Row>
              <Alert type="warning" size="small"></Alert>
              <AlertWithPopover data={popOverInfo(props.fph.processName)} />
            </Row>
          ) : (
            ""
          )}
        </div>
        <Row width="100%" widths={["grid-6", "grid-6"]}>
          <div>
            <Text type="b100">
              <FormattedMessage id="fph.target" defaultMessage="Target" />
            </Text>
            {props.fph.processName === INDUCT ? (
              <Text type="d50">
                {pvaData.targetData.INDUCT_FPH
                  ? pvaData.targetData.INDUCT_FPH
                  : 0}
              </Text>
            ) : (
              ""
            )}
            {props.fph.processName === SORT ? (
              <Text type="d50">
                {pvaData.targetData.STOW_FPH ? pvaData.targetData.STOW_FPH : 0}
              </Text>
            ) : (
              ""
            )}
            {props.fph.processName === PICK ? (
              <Text type="d50">
                {pvaData.targetData.PICK_FPH ? pvaData.targetData.PICK_FPH : 0}
              </Text>
            ) : (
              ""
            )}
            {props.fph.processName === STAGE ? (
              <Text type="d50">
                {pvaData.targetData.STAGE_FPH
                  ? pvaData.targetData.STAGE_FPH
                  : 0}
              </Text>
            ) : (
              ""
            )}
          </div>
          <div>
            <Text type="b100">
              <FormattedMessage id="fph.actual" defaultMessage="Actual" />
            </Text>
            {isActualsAlert(props.fph.processName) ? (
              <Text type="d50" className="alert">
                {viewModel.fph[props.fph.processName].actual}
              </Text>
            ) : (
              <Text type="d50">
                {viewModel.fph[props.fph.processName].actual}
              </Text>
            )}
          </div>
        </Row>
      </div>
    </Box>
  );
};

export default injectIntl(FPHView);



// WEBPACK FOOTER //
// ./src/components/FPH/FPHViewv2.js